import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Headphones,
  Link2,
  List,
  Mail,
  MessagesSquare,
  RotateCw,
  UserRound,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../../auth';
import type {
  ContactConversation,
  Ticket,
} from '../../app/legacy-types';
import { PageHeading } from '../../components/ui/PageHeading';
import { NewTicket, type NewTicketInitialDraft } from '../tickets/NewTicket';
import type { ContactChannelFilter } from './ChannelIdentity';
import { contactCenterApi, contactCenterKeys } from './contactCenterApi';
import { ConversationList } from './ConversationList';
import { ConversationThread } from './ConversationThread';
import { CustomerPanel } from './CustomerPanel';
import { ContactTicketPanel } from './ContactTicketPanel';
import { NewEmailComposer, type SentMailResult } from './NewEmailComposer';
import { ReplyComposer } from './ReplyComposer';
import {
  attachmentUrl,
  channelLabel,
  contactAttachments,
  filterConversations,
  formatContactDateTime,
  isImageAttachment,
  meaningfulConversationDescription,
  pickConversation,
  type ContactCenterFilters,
} from './contactCenterModel';

function EmptyState({ icon, title, text, compact = false }: { icon: ReactNode; title: string; text: string; compact?: boolean }) {
  return <div className={`empty-state ${compact ? 'compact' : ''}`}><span>{icon}</span><b>{title}</b><p>{text}</p></div>;
}

function PageSkeleton() {
  return <div className="page-skeleton"><span /><span /><span /><span /></div>;
}

type ImageLightboxItem = {
  id: string;
  src: string;
  downloadUrl: string;
  name: string;
  meta?: string;
};

function ImageLightbox({ items, initialIndex, onClose }: { items: ImageLightboxItem[]; initialIndex: number; onClose: () => void }) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [copied, setCopied] = useState(false);
  const item = items[activeIndex] ?? items[0];

  useEffect(() => {
    setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(0, items.length - 1)));
  }, [initialIndex, items.length]);

  useEffect(() => {
    setScale(1);
    setRotation(0);
    setCopied(false);
  }, [activeIndex]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && items.length > 1) setActiveIndex(index => (index - 1 + items.length) % items.length);
      if (event.key === 'ArrowRight' && items.length > 1) setActiveIndex(index => (index + 1) % items.length);
      if (event.key === '+' || event.key === '=') setScale(value => Math.min(4, value + .25));
      if (event.key === '-') setScale(value => Math.max(.35, value - .25));
      if (event.key.toLowerCase() === 'r') setRotation(value => value + 90);
      if (event.key === '0') { setScale(1); setRotation(0); }
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [items.length, onClose]);

  if (!item) return null;

  async function copyLink() {
    const url = new URL(item.src, window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const input = document.createElement('textarea');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  return <div className="image-lightbox" role="dialog" aria-modal="true" aria-label={`Просмотр изображения ${item.name}`} onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="image-lightbox-top">
      <div className="image-lightbox-title"><b title={item.name}>{item.name}</b>{item.meta && <small>{item.meta}</small>}</div>
      <div className="image-lightbox-tools">
        <button type="button" onClick={() => setScale(value => Math.max(.35, value - .25))} title="Уменьшить"><ZoomOut /></button>
        <span>{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => setScale(value => Math.min(4, value + .25))} title="Увеличить"><ZoomIn /></button>
        <button type="button" onClick={() => { setScale(1); setRotation(0); }} title="Исходный размер">1:1</button>
        <button type="button" onClick={() => setRotation(value => value + 90)} title="Повернуть"><RotateCw /></button>
        <button type="button" onClick={copyLink} title="Скопировать ссылку"><Link2 /></button>
        <a href={item.src} target="_blank" rel="noreferrer" title="Открыть оригинал"><ExternalLink /></a>
        <a href={item.downloadUrl} title="Скачать"><Download /></a>
        <button className="image-lightbox-close" type="button" onClick={onClose} title="Закрыть"><X /></button>
      </div>
    </div>
    {copied && <div className="image-lightbox-toast">Ссылка скопирована</div>}
    <div className="image-lightbox-stage" onWheel={event => { event.preventDefault(); setScale(value => Math.min(4, Math.max(.35, value + (event.deltaY < 0 ? .15 : -.15)))); }}>
      {items.length > 1 && <button className="image-lightbox-nav previous" type="button" onClick={() => setActiveIndex(index => (index - 1 + items.length) % items.length)} aria-label="Предыдущее изображение"><ChevronLeft /></button>}
      <img src={item.src} alt={item.name} draggable={false} style={{ transform: `scale(${scale}) rotate(${rotation}deg)` }} />
      {items.length > 1 && <button className="image-lightbox-nav next" type="button" onClick={() => setActiveIndex(index => (index + 1) % items.length)} aria-label="Следующее изображение"><ChevronRight /></button>}
    </div>
    <div className="image-lightbox-bottom"><span>{activeIndex + 1} / {items.length}</span><small>Колесо мыши — масштаб · ← → — переход · R — поворот · Esc — закрыть</small></div>
  </div>;
}

type ConversationCache = Array<readonly [readonly unknown[], ContactConversation[] | undefined]>;

export function ContactCenterPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const listStatus: 'ACTIVE' | 'CLOSED' = searchParams.get('status') === 'CLOSED' ? 'CLOSED' : 'ACTIVE';
  const selectedId = searchParams.get('conversation');
  const targetMessageId = searchParams.get('message');
  const legacyMailMessageId = searchParams.get('mailMessage') || '';
  const emailComposeOpen = searchParams.get('compose') === '1';
  const requestedChannel = String(searchParams.get('channel') || '').toUpperCase();
  const [mobileView, setMobileView] = useState<'list' | 'conversation' | 'customer'>(() => selectedId ? 'conversation' : 'list');
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<ContactChannelFilter>(() => {
    const requested = String(searchParams.get('channel') || '').toUpperCase();
    return ['TELEGRAM', 'MAX', 'EMAIL'].includes(requested) ? requested as ContactChannelFilter : 'ALL';
  });
  const [mineOnly, setMineOnly] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [linkTicketId, setLinkTicketId] = useState('');
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const [refreshFeedback, setRefreshFeedback] = useState('');
  const [imagePreviewIndex, setImagePreviewIndex] = useState<number | null>(null);
  const streamRef = useRef<HTMLDivElement | null>(null);

  const filters = useMemo<ContactCenterFilters>(() => ({
    status: listStatus,
    provider: channelFilter,
    mine: mineOnly || undefined,
    unread: unreadOnly || undefined,
    unanswered: unansweredOnly || undefined,
    search: search.trim(),
  }), [channelFilter, listStatus, mineOnly, search, unansweredOnly, unreadOnly]);

  const conversationsQuery = useQuery({
    queryKey: contactCenterKeys.conversations(filters),
    queryFn: ({ signal }) => contactCenterApi.conversations(filters, signal),
    refetchInterval: 5_000,
  });
  const conversations = conversationsQuery.data ?? [];
  const visibleConversations = useMemo(() => filterConversations(conversations, { provider: channelFilter, search }), [channelFilter, conversations, search]);
  const selected = pickConversation(visibleConversations, selectedId);

  const messagesQuery = useQuery({
    queryKey: contactCenterKeys.messages(selected?.id),
    queryFn: ({ signal }) => selected ? contactCenterApi.messages(selected.id, signal) : Promise.resolve([]),
    enabled: Boolean(selected?.id),
    refetchInterval: 5_000,
  });
  const legacyMailLinkQuery = useQuery({
    queryKey: ['contact-center', 'mail-link', legacyMailMessageId],
    queryFn: ({ signal }) => contactCenterApi.resolveMailMessage(legacyMailMessageId, signal),
    enabled: Boolean(legacyMailMessageId),
    retry: false,
  });
  const messages = messagesQuery.data ?? [];
  const activityQuery = useQuery({
    queryKey: contactCenterKeys.activity(selected?.id),
    queryFn: ({ signal }) => selected ? contactCenterApi.activity(selected.id, signal) : Promise.resolve([]),
    enabled: Boolean(selected?.id),
    refetchInterval: 5_000,
  });
  const { data: operators = [] } = useQuery({ queryKey: contactCenterKeys.operators, queryFn: ({ signal }) => contactCenterApi.operators(signal) });
  const { data: recentTickets = [] } = useQuery({ queryKey: ['contact-ticket-list'], queryFn: ({ signal }) => api<Ticket[]>('/tickets', { signal }) });

  const read = useMutation({
    mutationFn: contactCenterApi.markRead,
    onMutate: async conversationId => {
      await queryClient.cancelQueries({ queryKey: contactCenterKeys.conversationLists() });
      const previous = queryClient.getQueriesData<ContactConversation[]>({ queryKey: contactCenterKeys.conversationLists() }) as ConversationCache;
      queryClient.setQueriesData<ContactConversation[]>({ queryKey: contactCenterKeys.conversationLists() }, rows => rows?.map(item => item.id === conversationId ? { ...item, unread_count: 0 } : item));
      return { previous };
    },
    onError: (_reason, _conversationId, context) => context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() }),
  });

  const assign = useMutation({
    mutationFn: ({ conversationId, userId }: { conversationId: string; userId: string }) => contactCenterApi.assign(conversationId, userId),
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: contactCenterKeys.conversationLists() });
      const previous = queryClient.getQueriesData<ContactConversation[]>({ queryKey: contactCenterKeys.conversationLists() }) as ConversationCache;
      const operator = operators.find(item => item.id === variables.userId);
      queryClient.setQueriesData<ContactConversation[]>({ queryKey: contactCenterKeys.conversationLists() }, rows => rows?.map(item => item.id === variables.conversationId ? {
        ...item,
        assigned_user_id: variables.userId || null,
        assigned_user_name: operator?.full_name || null,
      } : item));
      return { previous };
    },
    onError: (_reason, _variables, context) => context?.previous.forEach(([key, value]) => queryClient.setQueryData(key, value)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() }),
  });

  const updateStatus = useMutation({
    mutationFn: ({ conversationId, nextStatus }: { conversationId: string; nextStatus: ContactConversation['status'] }) => contactCenterApi.setStatus(conversationId, nextStatus),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() });
      const remains = listStatus === 'CLOSED' ? variables.nextStatus === 'CLOSED' : variables.nextStatus !== 'CLOSED';
      if (!remains && selectedId === variables.conversationId) clearSelection();
    },
  });
  const linkTicket = useMutation({
    mutationFn: (ticketId: string) => selected ? contactCenterApi.linkTicket(selected.id, ticketId) : Promise.reject(new Error('Диалог не выбран')),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() }); setCreateTicketOpen(false); },
  });
  const removeConversation = useMutation({
    mutationFn: contactCenterApi.removeConversation,
    onSuccess: () => { clearSelection(); queryClient.invalidateQueries({ queryKey: contactCenterKeys.all }); },
  });

  function clearSelection() {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.delete('conversation');
      next.delete('message');
      return next;
    }, { replace: true });
  }

  function changeChannelFilter(nextChannel: ContactChannelFilter) {
    setChannelFilter(nextChannel);
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      if (nextChannel === 'ALL') next.delete('channel');
      else next.set('channel', nextChannel);
      next.delete('conversation');
      next.delete('message');
      return next;
    }, { replace: true });
  }

  useEffect(() => {
    const nextChannel = ['TELEGRAM', 'MAX', 'EMAIL'].includes(requestedChannel) ? requestedChannel as ContactChannelFilter : 'ALL';
    setChannelFilter(current => current === nextChannel ? current : nextChannel);
  }, [requestedChannel]);

  useEffect(() => {
    if (!legacyMailLinkQuery.data) return;
    setChannelFilter('EMAIL');
    setMobileView('conversation');
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('channel', 'EMAIL');
      next.set('conversation', legacyMailLinkQuery.data.conversationId);
      next.set('message', legacyMailLinkQuery.data.messageId);
      next.delete('mailMessage');
      next.delete('folder');
      return next;
    }, { replace: true });
  }, [legacyMailLinkQuery.data, setSearchParams]);

  useEffect(() => {
    if (conversationsQuery.isLoading) return;
    const next = pickConversation(visibleConversations, selectedId);
    if (next?.id === selectedId || (!next && !selectedId)) return;
    setSearchParams(current => {
      const params = new URLSearchParams(current);
      params.set('status', listStatus);
      if (next) params.set('conversation', next.id); else params.delete('conversation');
      params.delete('message');
      return params;
    }, { replace: true });
  }, [conversationsQuery.isLoading, listStatus, selectedId, setSearchParams, visibleConversations]);

  useEffect(() => {
    if (!selected) setMobileView('list');
  }, [selected?.id]);

  useEffect(() => {
    if (!selected) return;
    if (selected.unread_count > 0) read.mutate(selected.id);
    setLinkTicketId(selected.linked_ticket_id || '');
    setCreateTicketOpen(false);
  }, [selected?.id]);

  useEffect(() => {
    if (!targetMessageId || !messages.length) return;
    const timer = window.setTimeout(() => streamRef.current?.querySelector<HTMLElement>(`[data-message-id="${targetMessageId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
    return () => window.clearTimeout(timer);
  }, [messages, targetMessageId]);

  useEffect(() => {
    if (!streamRef.current || targetMessageId) return;
    const frame = window.requestAnimationFrame(() => { if (streamRef.current) streamRef.current.scrollTop = streamRef.current.scrollHeight; });
    return () => window.cancelAnimationFrame(frame);
  }, [messages.length, selected?.id, targetMessageId]);

  function choose(item: ContactConversation) {
    setMobileView('conversation');
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('status', listStatus);
      next.set('conversation', item.id);
      next.delete('message');
      return next;
    }, { replace: true });
    if (item.unread_count) read.mutate(item.id);
  }

  function switchConversationStatus(nextStatus: 'ACTIVE' | 'CLOSED') {
    if (nextStatus === listStatus) return;
    setSearch('');
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('status', nextStatus);
      next.delete('conversation'); next.delete('message');
      return next;
    }, { replace: true });
  }

  async function runMessageAction(task: () => Promise<unknown>) {
    try {
      return await task();
    } finally {
      if (selected?.id) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: contactCenterKeys.messages(selected.id) }),
          queryClient.invalidateQueries({ queryKey: contactCenterKeys.activity(selected.id) }),
          queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() }),
        ]);
      }
    }
  }

  function openEmailComposer() {
    setChannelFilter('EMAIL');
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('channel', 'EMAIL');
      next.set('compose', '1');
      return next;
    });
  }

  function closeEmailComposer() {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.delete('compose');
      next.delete('reply');
      next.delete('to');
      next.delete('subject');
      return next;
    }, { replace: true });
  }

  async function emailSent(result: SentMailResult) {
    setChannelFilter('EMAIL');
    setMobileView('conversation');
    await queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() });
    if (result.storedMailMessageId) {
      try {
        const link = await contactCenterApi.resolveMailMessage(result.storedMailMessageId);
        setSearchParams(current => {
          const next = new URLSearchParams(current);
          next.set('channel', 'EMAIL');
          next.set('conversation', link.conversationId);
          next.set('message', link.messageId);
          next.delete('compose');
          next.delete('reply');
          next.delete('to');
          next.delete('subject');
          return next;
        }, { replace: true });
      } catch {
        closeEmailComposer();
      }
    } else closeEmailComposer();
    setRefreshFeedback(result.storedInSent ? 'Письмо отправлено и добавлено в цепочку' : `Письмо отправлено${result.warning ? ` · ${result.warning}` : ''}`);
  }

  const contactImageGallery = useMemo<ImageLightboxItem[]>(() => messages.flatMap(currentMessage => contactAttachments(currentMessage).filter(isImageAttachment).map((attachment, index) => {
    const sourceIndex = attachment.sourceIndex ?? index;
    return {
      id: `${currentMessage.id}:${sourceIndex}`,
      src: attachmentUrl(currentMessage.id, sourceIndex),
      downloadUrl: attachmentUrl(currentMessage.id, sourceIndex, true),
      name: attachment.name || 'Изображение',
      meta: `${currentMessage.sender_name || (currentMessage.direction === 'OUTBOUND' ? 'Оператор' : 'Клиент')} · ${formatContactDateTime(currentMessage.created_at)}`,
    };
  })), [messages]);
  const contactImageIndex = useMemo(() => new Map(contactImageGallery.map((item, index) => [item.id, index])), [contactImageGallery]);
  const newTicketDraft = useMemo<NewTicketInitialDraft | undefined>(() => selected ? {
    companyId: selected.linked_company_id || undefined,
    contactId: selected.linked_contact_id || undefined,
    subject: `${channelLabel(selected.provider)} · ${selected.title}`,
    description: meaningfulConversationDescription(messages, selected.last_message_text || ''),
    requesterFullName: selected.customer_name || selected.title,
    requesterPhone: selected.customer_phone || undefined,
    requesterEmail: selected.provider.toUpperCase() === 'EMAIL' && selected.customer_external_id?.includes('@') ? selected.customer_external_id : undefined,
    category: channelLabel(selected.provider),
    contactConversationId: selected.id,
    contextDescription: `Заявка будет связана с диалогом ${channelLabel(selected.provider)}`,
  } : undefined, [messages, selected]);

  return <div className="contact-center-v3-page" data-mobile-view={mobileView}>
    <PageHeading eyebrow="КОНТАКТ-ЦЕНТР" title="Контакт-центр" description="Все обращения из Telegram, MAX и почты — в едином рабочем окне" actions={<><span className="refresh-feedback">{legacyMailLinkQuery.isError ? 'Не удалось открыть письмо по старой ссылке' : refreshFeedback}</span><button className="button primary" onClick={openEmailComposer}><Mail /> Новое письмо</button></>} />
    <nav className="contact-mobile-workspace-nav" aria-label="Навигация по обращению">
      {mobileView !== 'list' && <button type="button" aria-label="Вернуться к списку обращений" onClick={() => setMobileView('list')}><List /> Обращения</button>}
      {selected && mobileView === 'conversation' && <button type="button" aria-label="Открыть карточку собеседника" onClick={() => setMobileView('customer')}><UserRound /> Собеседник</button>}
      {selected && mobileView === 'customer' && <button type="button" aria-label="Вернуться к переписке" onClick={() => setMobileView('conversation')}><MessagesSquare /> Переписка</button>}
    </nav>
    <div className="contact-center-v2 contact-center-v3">
      <ConversationList
        rows={visibleConversations}
        selectedId={selected?.id}
        status={listStatus}
        channel={channelFilter}
        search={search}
        mineOnly={mineOnly}
        unreadOnly={unreadOnly}
        unansweredOnly={unansweredOnly}
        loading={conversationsQuery.isLoading}
        error={conversationsQuery.isError}
        onSelect={choose}
        onStatusChange={switchConversationStatus}
        onChannelChange={changeChannelFilter}
        onSearchChange={setSearch}
        onMineChange={setMineOnly}
        onUnreadChange={setUnreadOnly}
        onUnansweredChange={setUnansweredOnly}
        onRetry={() => { void conversationsQuery.refetch(); }}
      />

      <section className="surface contact-chat-panel v2">
        {selected ? <div className="contact-dialog-layout">
          <ConversationThread
            conversation={selected}
            messages={messages}
            activity={activityQuery.data ?? []}
            operators={operators}
            targetMessageId={targetMessageId}
            streamRef={streamRef}
            loading={messagesQuery.isLoading}
            error={messagesQuery.isError}
            pending={assign.isPending || updateStatus.isPending || removeConversation.isPending}
            imageIndexes={contactImageIndex}
            onPreview={setImagePreviewIndex}
            onRetry={() => { void messagesQuery.refetch(); }}
            onAssign={userId => assign.mutate({ conversationId: selected.id, userId })}
            onStatus={nextStatus => updateStatus.mutate({ conversationId: selected.id, nextStatus })}
            onOpenTicket={() => { if (selected.linked_ticket_id) navigate(`/tickets/${selected.linked_ticket_id}`); }}
            onDelete={() => { if (window.confirm('Удалить чат и всю переписку?')) removeConversation.mutate(selected.id); }}
            onEditMessage={(messageId, body) => runMessageAction(() => contactCenterApi.editMessage(messageId, body))}
            onDeleteMessage={messageId => runMessageAction(() => contactCenterApi.deleteMessageEverywhere(messageId))}
            onReactMessage={(messageId, reaction) => runMessageAction(() => contactCenterApi.reactToMessage(messageId, reaction))}
            onRetryAction={actionId => runMessageAction(() => contactCenterApi.retryAction(actionId))}
            composer={<ReplyComposer conversation={selected} onSent={() => {
              void queryClient.invalidateQueries({ queryKey: contactCenterKeys.messages(selected.id) });
              void queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() });
            }} />}
          />

          <aside className="contact-sidebar-panel">
            <CustomerPanel conversation={selected} />
            <ContactTicketPanel
              conversation={selected}
              tickets={recentTickets}
              selectedTicketId={linkTicketId}
              linking={linkTicket.isPending}
              onSelectionChange={setLinkTicketId}
              onLink={() => linkTicket.mutate(linkTicketId)}
              onUnlink={() => linkTicket.mutate('')}
              onOpen={() => { if (selected.linked_ticket_id) navigate(`/tickets/${selected.linked_ticket_id}`); }}
              onCreate={() => setCreateTicketOpen(true)}
            />
          </aside>
        </div> : <EmptyState icon={<Headphones />} title="Выберите обращение" text="Здесь будет отображаться переписка из подключённых каналов." />}
      </section>
    </div>
    {imagePreviewIndex !== null && <ImageLightbox items={contactImageGallery} initialIndex={imagePreviewIndex} onClose={() => setImagePreviewIndex(null)} />}
    {emailComposeOpen && <NewEmailComposer
      initialTo={searchParams.get('to') || ''}
      initialSubject={searchParams.get('subject') || ''}
      replyMessageId={searchParams.get('reply') || ''}
      onClose={closeEmailComposer}
      onSent={emailSent}
    />}
    {createTicketOpen && newTicketDraft && <NewTicket
      initialDraft={newTicketDraft}
      onClose={() => setCreateTicketOpen(false)}
      onCreated={async ticket => {
        if (user?.id) await api(`/tickets/${ticket.id}/assignees`, { method: 'POST', body: JSON.stringify({ userId: user.id, isPrimary: true }) }).catch(() => undefined);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: contactCenterKeys.conversationLists() }),
          queryClient.invalidateQueries({ queryKey: ['contact-ticket-list'] }),
        ]);
      }}
    />}
  </div>;
}
